// src/pages/About.js
import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import AskVillage from "../components/AskVillage";

const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";
const SAFFRON_PALE = "#FAEEDA";

// Daily rituals / offerings — same content, presented as scannable cards
const rituals = [
  {
    icon: "🪔",
    title: "Abhishekams",
    desc: "Sacred ceremonial bathing of the deity performed with devotion each day.",
  },
  {
    icon: "🔥",
    title: "Aartis",
    desc: "Evening rituals of light offered to the divine, uniting devotees in prayer.",
  },
  {
    icon: "🕉️",
    title: "Homams",
    desc: "Special fire rituals performed for the wellbeing of devotees and the village.",
  },
  {
    icon: "🍚",
    title: "Annadanam",
    desc: "Free meals offered to devotees and the community on auspicious occasions.",
  },
];

const festivals = [
  { icon: "🌙", label: "Maha Shivaratri" },
  { icon: "🪷", label: "Kartika Masam" },
];

export default function About() {
  const navigate = useNavigate();

  // AskVillage floating assistant — now available on the About page too,
  // not just Home. Each page that uses it needs its own isOpen state.
  const [isAskOpen, setIsAskOpen] = useState(false);

  return (
    <Box maxW="900px" mx="auto" p={{ base: 4, md: 8 }}>
      {/* ── Hero ── */}
      <VStack spacing={3} textAlign="center" mb={10}>
        <Box fontSize="40px" lineHeight="1">
          🛕
        </Box>
        <Heading fontSize={{ base: "2xl", md: "3xl" }} color={SAFFRON}>
          About the Temple
        </Heading>
        <Box w="48px" h="2px" bg={SAFFRON} borderRadius="full" opacity={0.6} />
      </VStack>

      {/* ── Intro ── */}
      <VStack spacing={5} align="start" mb={12}>
        <Box
          p={5}
          borderRadius="16px"
          bg={SAFFRON_PALE}
          border="1px solid"
          borderColor="orange.200"
        >
          <Text fontSize="md" lineHeight="1.85" color="gray.700">
            <strong>Sri Annapurna Sametha Visweswara Swamy Alayam</strong>,
            located in Thokada near Rajahmundry, is a sacred abode dedicated to
            the divine union of <strong>Lord Visweswara Swamy</strong> and{" "}
            <strong>Goddess Annapurna Devi</strong>. The temple is being built
            through the devotion and generous support of villagers and
            well-wishers who believe in preserving{" "}
            <strong>Sanatan Dharma</strong> and age-old{" "}
            <strong>Vedic values</strong>.
          </Text>
        </Box>

        <Text fontSize="md" lineHeight="1.85" color="gray.700">
          Grand celebrations take place during major festivals, uniting devotees
          in devotion and service throughout the year.
        </Text>

        {/* Festival chips */}
        <HStack spacing={3} flexWrap="wrap">
          {festivals.map((f) => (
            <HStack
              key={f.label}
              px={4}
              py={2}
              bg="white"
              border="1px solid"
              borderColor="orange.200"
              borderRadius="full"
              boxShadow="0 2px 8px rgba(133,79,11,0.08)"
            >
              <Text fontSize="lg">{f.icon}</Text>
              <Text fontSize="sm" fontWeight="600" color={SAFFRON_DARK}>
                {f.label}
              </Text>
            </HStack>
          ))}
        </HStack>
      </VStack>

      {/* ── Rituals grid ── */}
      <Box mb={12}>
        <Heading fontSize="lg" color={SAFFRON_DARK} mb={4}>
          Daily Rituals &amp; Offerings
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {rituals.map((r) => (
            <VStack
              key={r.title}
              p={4}
              bg="white"
              border="1px solid"
              borderColor="orange.100"
              borderRadius="14px"
              boxShadow="0 4px 14px rgba(133,79,11,0.08)"
              spacing={2}
              transition="transform 0.15s ease"
              _hover={{ transform: "translateY(-3px)" }}
            >
              <Text fontSize="28px">{r.icon}</Text>
              <Text
                fontWeight="700"
                fontSize="sm"
                color={SAFFRON_DARK}
                textAlign="center"
              >
                {r.title}
              </Text>
              <Text
                fontSize="xs"
                color="gray.600"
                textAlign="center"
                lineHeight="1.6"
              >
                {r.desc}
              </Text>
            </VStack>
          ))}
        </SimpleGrid>
      </Box>

      <Divider borderColor="orange.100" mb={10} />

      {/* ── Mission + CTA ── */}
      <VStack
        spacing={5}
        textAlign="center"
        p={{ base: 6, md: 8 }}
        borderRadius="20px"
        bg={`linear-gradient(135deg, ${SAFFRON}, ${SAFFRON_DARK})`}
        color="white"
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          lineHeight="1.85"
          maxW="640px"
        >
          This spiritual endeavor continues to thrive through the love and
          contributions of devotees across the globe. We humbly invite you to be
          part of this divine mission —{" "}
          <strong>
            your donation, small or large, helps us complete the construction
            and serve devotees with dignity and devotion.
          </strong>
        </Text>

        <Text fontSize="md" fontWeight="700">
          🕉️ Let's build a sacred space together — brick by brick, prayer by
          prayer.
        </Text>

        <Button
          size="lg"
          bg="white"
          color={SAFFRON_DARK}
          fontWeight="700"
          borderRadius="full"
          px={8}
          _hover={{ bg: "orange.50", transform: "translateY(-2px)" }}
          transition="all 0.18s ease"
          onClick={() => navigate("/donate")}
        >
          🙏 Support the Temple
        </Button>
      </VStack>

      {/* ── GramadevaAI floating assistant — now on About page too ── */}
      <AskVillage isOpen={isAskOpen} setIsOpen={setIsAskOpen} />

      {/* Always-visible label near the button on mobile, since the
          Tooltip inside AskVillage only shows on md+ screens. Placed
          here (not inside AskVillage) so it doesn't affect Home page. */}
      {!isAskOpen && (
        <Box
          position="fixed"
          bottom="88px"
          right="20px"
          zIndex="9998"
          display={{ base: "block", md: "none" }}
          pointerEvents="none"
        >
          <Text
            fontSize="10px"
            fontWeight="700"
            color={SAFFRON_DARK}
            bg="white"
            px={2}
            py={1}
            borderRadius="full"
            boxShadow="0 2px 6px rgba(0,0,0,0.15)"
            textAlign="center"
          >
            Ask AI
          </Text>
        </Box>
      )}
    </Box>
  );
}
